// Down Payment / Adjustments credits -> MISMO 3.4 LOAN/PURCHASE_CREDITS.
//
// Each credit becomes one <PURCHASE_CREDIT> with an amount, a PurchaseCreditType
// and a PurchaseCreditSourceType (who gave it). For the named credits the source
// is derived (Seller Concessions -> PropertySeller, Real Estate Credit ->
// RealEstateAgent, Lender Credit -> Lender, etc.); the "Other Assets - Credits"
// items (Lot/Rent/Sweat/Trade Equity) carry the source the LO picked.
//
// Where a credit has no specific MISMO type it exports as Type="Other" with the
// LANIS label in PurchaseCreditTypeOtherDescription, so the name always survives.
// Zero amounts are skipped. Purchase only.

import Decimal from "decimal.js";

import { PURCHASE } from "../../constants/document_types";
import { SOURCE_TO_MISMO } from "../../constants/purchase_credit_sources";
import {
  emd,
  seller_concessions,
  real_estate_credit
} from "../../calculations/loan";

function amt(value) {
  const n = value instanceof Decimal ? value : new Decimal(parseFloat(value) || 0);
  return n.toDecimalPlaces(2);
}

// Resolve a LO-selected source key to its MISMO enum, else a default.
function sourceMismo(sourceKey, fallback) {
  return SOURCE_TO_MISMO[sourceKey] || fallback;
}

export function skipPurchaseCredits({ document, loan, options }) {
  const documentType = options && options.documentType;
  return documentType !== PURCHASE || !loan;
}

export function populatePurchaseCredits(builder, { document, options, loan }) {
  const documentType = options && options.documentType;
  if (documentType !== PURCHASE || !loan) return;

  const property = document.property;
  const oa = loan.other_adjustments || {};

  // { amount, type, source (MISMO), label } — label sets OtherDescription when
  // type is "Other".
  const credits = [
    {
      amount: emd(documentType, loan, property),
      type: "EarnestMoney",
      source: "BorrowerPaidOutsideClosing",
      label: "Earnest Money Deposit"
    },
    {
      amount: seller_concessions(documentType, loan, property),
      type: "Other",
      source: "PropertySeller",
      label: "Seller Credit"
    },
    {
      amount: real_estate_credit(documentType, loan, property),
      type: "Other",
      source: "RealEstateAgent",
      label: "Real Estate Credit"
    },
    {
      amount: oa.lender_credit,
      type: "Other",
      source: "Lender",
      label: "Lender Credit"
    },
    {
      amount: oa.employer_assisted_housing,
      type: "EmployerAssistedHousing",
      source: "Employer",
      label: "Employer Assisted Housing"
    },
    {
      amount: oa.relocation_funds,
      type: "RelocationFunds",
      source: "Employer",
      label: "Relocation Funds"
    },
    {
      amount: oa.lease_purchase_fund,
      type: "LeasePurchaseFund",
      source: "PropertySeller",
      label: "Lease Purchase Fund"
    },
    {
      amount: oa.cash_deposit_on_sales_contract,
      type: "EarnestMoney",
      source: "BorrowerPaidOutsideClosing",
      label: "Cash Deposit on Sales Contract"
    },
    {
      amount: oa.borrower_paid_fees,
      type: "Other",
      source: "BorrowerPaidOutsideClosing",
      label: "Borrower Paid Fees"
    },
    {
      amount: oa.lot_equity,
      type: "Other",
      source: sourceMismo(oa.lot_equity_source, "Other"),
      label: "Lot Equity"
    },
    {
      amount: oa.rent_credit,
      type: "Other",
      source: sourceMismo(oa.rent_credit_source, "PropertySeller"),
      label: "Rent Credit"
    },
    {
      amount: oa.sweat_equity,
      type: "SweatEquity",
      source: sourceMismo(oa.sweat_equity_source, "Other"),
      label: "Sweat Equity"
    },
    {
      amount: oa.trade_equity,
      type: "TradeEquity",
      source: sourceMismo(oa.trade_equity_source, "Other"),
      label: "Trade Equity"
    }
  ];

  credits.forEach(({ amount, type, source, label }) => {
    const value = amt(amount);
    if (value.lessThanOrEqualTo(0)) return;

    const credit = builder.ele("PURCHASE_CREDIT");
    credit.ele("PurchaseCreditAmount").txt(value.toString());
    credit.ele("PurchaseCreditSourceType").txt(source);
    credit.ele("PurchaseCreditType").txt(type);
    if (type === "Other" && label) {
      credit.ele("PurchaseCreditTypeOtherDescription").txt(label);
    }
  });
}
